import React, { useCallback, useEffect, useState } from 'react'
import { RiNotificationLine, RiFullscreenFill, RiLogoutCircleLine } from 'react-icons/ri';
import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../config/api';

const NOTIFICATIONS_URL = `${API_BASE_URL}/api/v1/notifications`;

const getNotificationId = (notification) =>
  notification?.id ??
  notification?.notification_id ??
  notification?.pk ??
  notification?.notification?.id ??
  null;

const isNotificationUnread = (notification) => {
  if (!notification) return false;
  if (notification.read_at) return false;
  if (
    notification.is_read === true ||
    notification.read === true ||
    notification.seen === true
  ) {
    return false;
  }
  if (
    notification.is_read === false ||
    notification.read === false ||
    notification.seen === false ||
    notification.is_unread === true ||
    notification.unread === true
  ) {
    return true;
  }
  if (typeof notification.status === 'string') {
    const status = notification.status.toLowerCase();
    if (status === 'read' || status === 'seen') return false;
    if (status === 'unread' || status === 'new') return true;
  }
  return true;
};

const sameNotificationId = (a, b) => String(a) === String(b);

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Topbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const userCookie = Cookies.get('user');
  let user = null;
  try {
    user = userCookie ? JSON.parse(userCookie) : null;
  } catch (e) {
    user = null;
  }

  const getAuthHeaders = () => {
    const token = Cookies.get('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${NOTIFICATIONS_URL}/unread-count/`, {
        headers: getAuthHeaders(),
      });
      const count =
        response.data?.unread_count ??
        response.data?.count ??
        response.data?.unreadCount ??
        0;
      setUnreadCount(Number(count) || 0);
    } catch (error) {
      console.error('Error fetching unread notification count:', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const response = await axios.get(`${NOTIFICATIONS_URL}/`, {
        headers: getAuthHeaders(),
      });
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.notifications)
            ? data.notifications
            : [];
      setNotifications(list);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    refreshNotifications();
    const intervalId = setInterval(refreshNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [refreshNotifications]);

  const markNotificationReadLocally = (notificationId) => {
    setNotifications((prev) =>
      prev.map((item) =>
        sameNotificationId(getNotificationId(item), notificationId)
          ? {
              ...item,
              is_read: true,
              read: true,
              seen: true,
              is_unread: false,
              unread: false,
              status: 'read',
              read_at: item.read_at || new Date().toISOString(),
            }
          : item
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (notificationId == null || notificationId === '') return;

    const headers = getAuthHeaders();
    const url = `${NOTIFICATIONS_URL}/${notificationId}/mark-read/`;

    try {
      try {
        await axios.patch(url, {}, { headers });
      } catch (patchError) {
        const status = patchError?.response?.status;
        if (status === 405 || status === 404 || status === 501) {
          await axios.post(url, {}, { headers });
        } else {
          throw patchError;
        }
      }

      markNotificationReadLocally(notificationId);
      await fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const headers = getAuthHeaders();
    const url = `${NOTIFICATIONS_URL}/mark-all-read/`;

    try {
      try {
        await axios.post(url, {}, { headers });
      } catch (postError) {
        const status = postError?.response?.status;
        if (status === 405 || status === 404 || status === 501) {
          await axios.patch(url, {}, { headers });
        } else {
          throw postError;
        }
      }

      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          is_read: true,
          read: true,
          seen: true,
          is_unread: false,
          unread: false,
          status: 'read',
          read_at: item.read_at || new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleLogout = () => {
    Cookies.remove('token');
    navigate('/');
  }

  const isFirstTimeLogin =
    user?.first_login??
    user?.firstLogin ??
    user?.isFirstLogin ??
    user?.isFirstTimeLogin ??
    user?.mustChangePassword ??
    user?.forcePasswordChange;

  const mustForceChangePassword =
    isFirstTimeLogin === true || isFirstTimeLogin === 'true' || isFirstTimeLogin === 1;

  useEffect(() => {
    if (!mustForceChangePassword) return;
    if (location.pathname === '/manager/change-password') return;
    navigate('/manager/change-password', { replace: true });
  }, [location.pathname, mustForceChangePassword, navigate]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div>
      <>{ /* app-header */ }
        <div className="app-header-area">
            <header className="app-header" id="header">
                <div className="app-header-inner">
                    <div className="app-header-left">
                        <div className="d-flex align-center gap-15">
                            <div className="app-header-element">
                                <a className="sidebar-toggle-bar" id="sidebarToggle" href="javascript:void(0);">
                                    <div className="sidebar-menu-bar">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </a>
                            </div>
                            <div className="app-header-ls-logo">
                                { /* large screen logo */ }
                                <a className="app-header-ls-dark-logo" href="/">
                                    <img src="/assets/images/burray/burray.png" alt="image" />
                                </a>
                                <a className="app-header-ls-light-logo" href="/">
                                    <img src="/assets/images/burray/burray.png" alt="image" />
                                </a>
                            </div>
                            <div className="app-header-mobile-logo">
                                <a className="app-header-dark-logo" href="/">
                                    <img src="/assets/images/burray/burray.png" alt="image" />
                                </a>
                                <a className="app-header-light-logo" href="/">
                                    <img src="/assets/images/burray/burray.png" alt="image" />
                                </a>
                            </div>
                        </div>
                        
                    </div>
                    <div className="app-header-right">
                        <div className="app-header-search-modal">
                            <button type="button" className="app-header-circle" data-bs-toggle="modal" data-bs-target="#searchModal">
                                <i className="ri-search-line" />
                            </button>
                        </div>
                        


                        <div className="app-header-fullscreen app-header-circle cursor-pointer">
                            <div onClick={toggleFullScreen}>
                                <RiFullscreenFill />
                            </div>
                        </div>

                        <div className="app-header-notification">
                            <div className="dropdown">
                                <a
                                  className="dropdown-toggle"
                                  href="javascript:void(0);"
                                  role="button"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  onClick={refreshNotifications}
                                >
                                    <span className="app-header-circle position-relative">
                                      <RiNotificationLine />
                                      {unreadCount > 0 && (
                                        <span
                                          className="badge bg-danger rounded-pill"
                                          style={{
                                            position: 'absolute',
                                            top: '-4px',
                                            right: '-4px',
                                            fontSize: '10px',
                                            minWidth: '16px',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0 4px',
                                          }}
                                        >
                                          {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                      )}
                                    </span>
                                </a>
                                <ul className="dropdown-menu">
                                    <li className="dropdown-menu-header">
                                        <h5>Notification</h5>
                                        {unreadCount > 0 && (
                                          <span className="badge bg-label-primary">
                                            {unreadCount} New
                                          </span>
                                        )}
                                    </li>
                                    <li className="dropdown-notifications-list card-scrollbar">
                                        <ul>
                                            {loadingNotifications && notifications.length === 0 ? (
                                              <li className="dropdown-notifications-list-item">
                                                <div className="content">
                                                  <p className="mb-0 text-body-secondary">Loading notifications...</p>
                                                </div>
                                              </li>
                                            ) : notifications.length === 0 ? (
                                              <li className="dropdown-notifications-list-item">
                                                <div className="content">
                                                  <p className="mb-0 text-body-secondary">No notifications yet</p>
                                                </div>
                                              </li>
                                            ) : (
                                              notifications.map((notification) => {
                                                const notificationId = getNotificationId(notification);
                                                const unread = isNotificationUnread(notification);
                                                return (
                                                  <li
                                                    key={notificationId ?? notification.title}
                                                    className="dropdown-notifications-list-item"
                                                    style={{
                                                      cursor: unread ? 'pointer' : 'default',
                                                      opacity: unread ? 1 : 0.75,
                                                    }}
                                                    onClick={(e) => {
                                                      if (unread) handleMarkAsRead(notificationId, e);
                                                    }}
                                                  >
                                                    <div className="content" style={{ flex: 1, minWidth: 0 }}>
                                                      <h6 className="mb-5">
                                                        {notification.title || 'Notification'}
                                                      </h6>
                                                      <p className="mb-5">{notification.message}</p>
                                                      <span className="text-body-secondary">
                                                        {formatRelativeTime(notification.created_at)}
                                                      </span>
                                                    </div>
                                                    {unread && (
                                                      <div className="notifications-actions d-flex direction-column align-center">
                                                        <button
                                                          type="button"
                                                          className="dropdown-notifications-read d-block pt-5 border-0 bg-transparent p-0"
                                                          title="Mark as read"
                                                          onClick={(e) => handleMarkAsRead(notificationId, e)}
                                                        >
                                                          <span className="bullet bg-primary" />
                                                        </button>
                                                      </div>
                                                    )}
                                                  </li>
                                                );
                                              })
                                            )}
                                        </ul>
                                    </li>
                                    <li className="dropdown-notifications-btn">
                                        <a
                                          className="btn btn-primary w-100"
                                          href="#"
                                          onClick={handleMarkAllAsRead}
                                          style={{
                                            pointerEvents: unreadCount === 0 ? 'none' : 'auto',
                                            opacity: unreadCount === 0 ? 0.6 : 1,
                                          }}
                                        >
                                          Mark all as read
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="app-header-user">
                            <div className="dropdown">
                                <a className="dropdown-toggle" href="javascript:void(0);" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    <div className="author">
                                        <div className="author-thumb">
                                            <img src="/assets/images/avatar/avatar-thumb-dummy.png" alt="user" />
                                        </div>
                                        <h6 className="author-name lh-1">Manager</h6>
                                    </div>
                                </a>
                                <ul className="dropdown-menu">
                                    <li className="bd-user-info-list"><a href="#" onClick={handleLogout}> &nbsp;&nbsp;<RiLogoutCircleLine />Logout</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <div className="body__overlay" />
        </div>
        { /* app-header */ }</>
    </div>
  )
}

export default Topbar
